import { AfterViewInit, Component, Inject, OnDestroy, Output, ViewChild, EventEmitter } from "@angular/core";
import { ReminderService } from "./reminders.service";
import { LogService } from "../logs/log.service";
import { Reminder, ReminderViewModel } from "./reminder.model";
import { catchError, exhaustMap, map, takeUntil } from "rxjs/operators";
import { ResponsiveOptions } from "../core/image-viewer/image-viewer.model";
import { Carousel } from "primeng/carousel";
import { of, Subject, Subscription, timer } from "rxjs";
import { ILocationService } from "angular";

@Component({
	templateUrl: './reminder-ticker.component.html',
	styleUrls: ['./reminder-ticker.component.css'],
	selector: 'reminder-ticker'
})
export class ReminderTickerComponent implements AfterViewInit, OnDestroy {
	
	@ViewChild('carousel') carousel?: Carousel;

	@Output() remindersChange = new EventEmitter<Reminder[]>()
	
	constructor(
		private service: ReminderService,
		@Inject('$location') private $location: ILocationService
	) {}
	

	responsiveOptions: (ResponsiveOptions & { numScroll: number })[] = [
		{
			breakpoint: '1200px',
			numVisible: 3,
			numScroll: 3
		},
		{
			breakpoint: '768px',
			numVisible: 2,
			numScroll: 2
		},
		{
			breakpoint: '560px',
			numVisible: 1,
			numScroll: 1
		}
	]
	
	initialRefresh = true
	userChangedCollapse = false
	hasMultiplePages = false
	refreshEnabled = true
	styleClass = ''
	
	private _autoPlayInterval = 12000;
	private _reminders: ReminderViewModel[] = []
	private destroy$ = new Subject();
	private timerSub?: Subscription
	
	get autoPlayInterval() {
		// Returns 0 if autoPlay should be disabled due to Carousel taking updates to
		// autoPlayInterval as a reason to re-enable autoPlay.
		return (this.hasMultiplePages) ? this._autoPlayInterval : 0;
	}
	
	set autoPlayInterval(val) {
		this._autoPlayInterval = val;
	}

	get reminders() {
		return this._reminders;
	}

	set reminders(val) {
		this._reminders = val;
		this.remindersChange.emit(Array.from(val));
	}
	
	private get autoPlayDisabled(): boolean {
		return !!this.$location.search()['autoPlayDisabled']
	}
	private get autoRefreshEnabled() {
		return !this.$location.search()['reminderRefreshDisabled'] && this.refreshEnabled
	}
	
	ngAfterViewInit(): void {
		// Autoplay is initially disabled
		this.toggleAutoPlay(false);
		
		this.initialRefresh = true
		this.createPoll()
	}
	
	ngOnDestroy(): void {
		this.destroy$.next()
	}
	
	getAutoplayInterval(): number {
		switch (this.carousel?.numVisible ?? -1)
		{
			case 2:
				return 8000
			case 1:
				return 5000
			default:
				return 12000
				
		}
	}
	
	createPoll(): Subscription {
		this.timerSub?.unsubscribe();
		return this.timerSub = timer(0, 10000)
			.pipe(
				takeUntil(this.destroy$),
				exhaustMap(() => {
					return (this.autoRefreshEnabled)
						? this.service.fetchRemindersOnTicker()
							.pipe(
								catchError((err) => {
									console.error(err);
									return of(this.reminders);
								}),
								map(r => ReminderService.mapToViewModel(r))
							)
						: of(this.reminders)
				})
			)
			.subscribe(reminders => {
				this.initialRefresh = false
				this.reminders = reminders
				this.hasMultiplePages = this.reminders.length > (this.carousel?.numVisible ?? 1)
				this.styleClass = !this.hasMultiplePages ? 'no-pages' : ''
				
				setTimeout(() => {
					this.autoPlayInterval = this.getAutoplayInterval();
					this.toggleAutoPlay(this.hasMultiplePages);
				})
			})
	}
	
	toggleAutoPlay(enabled: boolean) {
		if (!this.carousel)
			return;
		
		if (enabled && !this.autoPlayDisabled && !this.carousel.allowAutoplay && this.hasMultiplePages)
		{
			this.carousel.allowAutoplay = true;
			this.carousel.startAutoplay();
		}
		else if (!enabled || this.autoPlayDisabled)
		{
			this.carousel.allowAutoplay = false;
			this.carousel.stopAutoplay();
		}
	}
	
	goToReminderDetails(reminder: Reminder) {
		const index = this.reminders.findIndex(r => r.id === reminder.id);
		
		this.$location.url('/reminder-detail')
			.search('reminder', reminder._links.self.href)
			.search('query', JSON.stringify({
				onTicker: true,
				log: LogService.selectedLog?.id
			}))
			.search('query-format', 'A')
			.search('result-format', 'B')
			.search('index', index)
	}
}